import AlertIcon from '@tradera/blueprint/theme/icons2/alert.svg?jsx';
import InfoIcon from '@tradera/blueprint/theme/icons2/info.svg?jsx';
import CheckIcon from '@tradera/blueprint/theme/icons2/check.svg?jsx';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';
import ArrowRightIcon from 'tradera/blueprint/theme/icons2/arrow-right.svg?jsx';
import CloseIcon from 'tradera/blueprint/theme/icons2/close.svg?jsx';
import styles from './info-box.module.scss';
import { classNames } from 'tradera/utils/class-names';
import { Heading } from 'tradera/components/atoms/heading/heading';
import { Space } from 'tradera/components/atoms/space/space';
import { Text } from 'tradera/components/atoms/text/text';
import { MaybeLink } from 'tradera/components/maybe-link/maybe-link';
import { Button } from 'tradera/components/atoms/button/button';

type LoadingSpinIconProps = {
    className?: string;
};
const LoadingSpinIcon = ({ className }: LoadingSpinIconProps) => (
    <LoadingIcon className={`icon-spin ${className}`} />
);

const CloseButton = ({ onClick }: { onClick: () => void }) => (
    <div className={styles.closeButton}>
        <Button theme="text" round onClick={onClick}>
            <CloseIcon className={classNames('icon icon-sm icon-gray-800')} />
        </Button>
    </div>
);

type InfoBoxProps = React.PropsWithChildren<{
    header?: string;
    className?: string;
    info?: boolean;
    check?: boolean;
    success?: boolean;
    alert?: boolean;
    loading?: boolean;
    danger?: boolean;
    headerClassName?: string;
    actions?: React.ReactNode | React.ReactNode[];
    customIcon?: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
    children?: React.ReactNode;
    isFullWidth?: boolean;
}> &
    (
        | {
              onDismiss?: () => void;
              href?: never;
          }
        | {
              onDismiss?: never;
              href?: string;
          }
    );

export const InfoBox = ({
    header,
    className,
    info,
    check,
    success,
    alert,
    loading,
    danger,
    customIcon,
    headerClassName,
    actions,
    onDismiss,
    href,
    children,
    isFullWidth = false,
}: InfoBoxProps) => {
    let Icon = InfoIcon;
    if (customIcon) Icon = customIcon;
    else if (info) Icon = InfoIcon;
    else if (check || success) Icon = CheckIcon;
    else if (alert) Icon = AlertIcon;
    else if (loading) Icon = LoadingSpinIcon;

    return (
        <MaybeLink href={href}>
            <Space
                justify="between"
                className={classNames(
                    'position-relative',
                    styles.infoBox,
                    {
                        [styles.danger]: danger,
                        [styles.success]: success,
                    },
                    className,
                )}
                role="alert">
                <Space
                    className={classNames(isFullWidth && 'w-100')}
                    direction={header ? 'vertical' : 'horizontal'}
                    align="start"
                    gap={header && children ? 1 : children ? 2 : 0}>
                    <Space
                        gap={1}
                        className={classNames(isFullWidth && 'w-100')}>
                        <Icon className="icon icon-md flex-shrink-0 icon-gray-800" />
                        {header ? (
                            <Heading
                                as="h4"
                                size="amsterdam"
                                className={classNames(
                                    'font-weight-normal',
                                    onDismiss && 'mr-3',
                                    headerClassName,
                                )}
                                branded>
                                {header}
                            </Heading>
                        ) : null}
                        {onDismiss && <CloseButton onClick={onDismiss} />}
                    </Space>
                    <Space
                        direction="vertical"
                        gap={2}
                        className={classNames(isFullWidth && 'w-100')}>
                        {children ? (
                            <Text
                                as={typeof children === 'string' ? 'p' : 'span'}
                                color="gray-700"
                                className={onDismiss && !header ? 'mr-3' : ''}>
                                {children}
                            </Text>
                        ) : null}
                        {actions && <Space gap={1}>{actions}</Space>}
                    </Space>
                </Space>
                {href && !onDismiss && (
                    <ArrowRightIcon className="icon icon-md icon-gray-800" />
                )}
            </Space>
        </MaybeLink>
    );
};
